/* eslint-disable functional/immutable-data */

import type { ComponentProps } from 'react';
import React from 'react';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import type { ViewProps, ViewStyle } from 'react-native';
import { StyleSheet, View, ScrollView, Pressable } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { theme } from '@garnish/constants';
import { useReduceMotionStatus, useResponsive } from '../../hooks';

// Transparent variation of modals where it uses react-navigation instead of react-native.
export const TransparentModal = ({
  testID,
  size,
  children,
  transparent,
  disableAnimation,
  style,
  onClose,
  mobileEdges,
}: ModalProps) => {
  const { currentBreakpoint } = useResponsive();

  if (currentBreakpoint.isMobile) {
    return (
      <SafeAreaView
        testID={testID}
        style={styles.nativeDialog}
        edges={mobileEdges}
      >
        {children}
      </SafeAreaView>
    );
  }

  const wrapperSmStyles = [styles.wrapperSM, StyleSheet.absoluteFill, style];

  return (
    <View testID={testID} style={wrapperSmStyles}>
      <Backdrop onClose={onClose} />
      <AnimatedModal
        size={size}
        disableAnimation={disableAnimation}
        style={transparent ? styles.transparentDialog : undefined}
      >
        {children}
      </AnimatedModal>
    </View>
  );
};

const AnimatedModal = ({
  size,
  style,
  children,
  disableAnimation,
}: ViewProps & Pick<ModalProps, 'disableAnimation' | 'size'>) => {
  const { minWidth } = useResponsive();
  const reduceMotionEnabled = useReduceMotionStatus();
  const noAnimation = reduceMotionEnabled === 'disabled' || disableAnimation;
  const distance = size === 'drawer' ? DRAWER_ANIM_DISTANCE : ANIM_DISTANCE;
  const position = useSharedValue(disableAnimation ? 0 : distance);

  React.useEffect(() => {
    if (reduceMotionEnabled === 'disabled') return;

    position.value = withTiming(0, {
      duration: ANIM_DURATION,
      easing: Easing.out(Easing.exp),
    });
  }, [position, noAnimation, reduceMotionEnabled]);

  const initialPosition =
    size === 'drawer' ? styles.animatedDrawer : styles.animatedDialog;

  // ─── STYLES ──────────────────────────────────────────────────────

  const sizesWithSpacing: readonly ModalSize[] = [
    'xsmall',
    'small',
    'medium',
    'large',
  ];
  const shouldAddSpacing = minWidth.tablet && sizesWithSpacing.includes(size);
  const dialogWrapperSpacingStyles = shouldAddSpacing
    ? styles.dialogWrapperWithSpacing
    : {};
  const dialogWrapperStyles = [
    styles.dialogWrapper,
    dialogWrapperSpacingStyles,
  ];

  const dialogAnimatedStyle = useAnimatedStyle(() => {
    const translateModal = { translateY: position.value };
    const translateDrawer = { translateX: position.value };
    const translate = size === 'drawer' ? translateDrawer : translateModal;

    return { transform: [translate] };
  });
  const dialogStyles = [
    styles.dialog,
    modalDialogSizeStyles[size],
    !noAnimation && initialPosition,
    dialogAnimatedStyle,
  ];

  // ─────────────────────────────────────────────────────────────────

  return (
    <View style={dialogWrapperStyles} pointerEvents="box-none">
      <Animated.View style={dialogStyles}>
        <ScrollView
          contentContainerStyle={styles.contentContainer}
          style={[styles.dialogColor, style]}
        >
          {children}
        </ScrollView>
      </Animated.View>
    </View>
  );
};

const Backdrop = React.forwardRef<View, BackdropProps>(({ onClose }, ref) => (
  <Pressable
    accessibilityRole="button"
    ref={ref}
    testID="sg-react-navigation-modal-backdrop"
    style={[StyleSheet.absoluteFill, styles.backdrop]}
    onPress={onClose}
  />
));

// ────────────────────────────────────────────────────────────────────────────────

const ANIM_DURATION = 1000; // 1sec
const ANIM_DISTANCE = 15; // 15px
const DRAWER_ANIM_DISTANCE = 480; // 480px

const styles = StyleSheet.create({
  wrapperSM: {
    justifyContent: 'center',
  },

  // ──────── Sizes ─────────
  dialogSizeXSmall: {
    width: 460,
  },
  dialogSizeSmall: {
    width: 592,
  },
  dialogSizeMedium: {
    width: 800,
  },
  dialogSizeLarge: {
    width: '90%',
    height: '90%',
  },
  dialogSizeFull: {
    flex: 1,
    width: '100%',
    height: '100%',
    padding: 0,
    borderRadius: 0,
    borderTopRightRadius: 0,
    borderTopLeftRadius: 0,
  },
  dialogSizeDrawer: {
    flex: 1,
    width: 480,
    maxWidth: '100%',
    height: '100%',
    padding: 0,
    borderRadius: 0,
    borderTopRightRadius: 0,
    borderTopLeftRadius: 0,
    position: 'absolute',
    right: 0,
  },

  // ──────── Dialog ─────────
  dialog: {
    overflow: 'hidden',
    maxWidth: '100%',
    maxHeight: '100%',
    borderRadius: theme.radius.medium,
    margin: 'auto',
    ...theme.elevations['2'],
  },
  dialogWrapper: {
    flexGrow: 1,
    maxHeight: '100%',
  },
  dialogWrapperWithSpacing: {
    padding: theme.spacing.xlarge,
  },
  nativeDialog: {
    flex: 1,
    backgroundColor: theme.colors.OATMEAL,
  },
  animatedDialog: {
    maxHeight: '100%',
    transform: [{ translateY: ANIM_DISTANCE }],
  },
  animatedDrawer: {
    transform: [{ translateX: DRAWER_ANIM_DISTANCE }],
  },
  contentContainer: {
    maxHeight: '100%',
    flexGrow: 1,
  },
  dialogColor: {
    backgroundColor: theme.colors.OATMEAL,
  },
  transparentDialog: {
    backgroundColor: theme.colors.OPACITY.TRANSPARENT,
  },

  // ──────── Backdrop ─────────
  backdrop: {
    backgroundColor: theme.colors.OPACITY.GREEN_1.DARKER,
  },
});

// ────────────────────────────────────────────────────────────────────────────────

const modalDialogSizeStyles: Record<ModalSize, ViewStyle> = {
  xsmall: styles.dialogSizeXSmall,
  small: styles.dialogSizeSmall,
  medium: styles.dialogSizeMedium,
  large: styles.dialogSizeLarge,
  full: styles.dialogSizeFull,
  drawer: styles.dialogSizeDrawer,
};

// ────────────────────────────────────────────────────────────────────────────────

type BackdropProps = { readonly onClose: () => void };

type ModalSize =
  | 'xsmall' // Smaller Alerts
  | 'small' // Pickers, Small Alerts
  | 'medium' // Larger Warnings
  | 'large' // In-Modal Screens Sections, Forms. By default, "large" will be rendered as fullscreen on mobile devices.
  | 'full' // Fullscreen In-Modal Content
  | 'drawer'; // Full Height, animated from the right

type ModalProps = ActionProps &
  ViewProps &
  SizeProps & {
    readonly mobileEdges?: ComponentProps<typeof SafeAreaView>['edges'];
  };

type SizeProps = {
  readonly size: ModalSize;
  readonly transparent?: boolean;
  readonly disableAnimation?: boolean;
};

type ActionProps = { readonly onClose: () => void };
